import React, { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  DataTable,
  Button,
  Text,
  BlockStack,
  InlineStack,
  EmptyState,
  Spinner,
  Modal,
  Toast,
  TextField,
  FormLayout,
  Badge,
  Box,
  Select,
} from '@shopify/polaris';
import { api } from '../api';

const PER_PAGE_OPTIONS = [
  { label: '5 per page', value: '5' },
  { label: '10 per page', value: '10' },
  { label: '25 per page', value: '25' },
  { label: '50 per page', value: '50' },
];

function PaginationBar({ page, totalPages, total, start, end, onPrev, onNext }) {
  if (total === 0) return null;
  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <Text variant="bodySm" tone="subdued">
          Showing {start}–{end} of {total} item{total !== 1 ? 's' : ''}
        </Text>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button onClick={onPrev} disabled={page <= 1} style={btnStyle(page > 1)}>← Previous</button>
          <Text variant="bodySm" tone="subdued">Page {page} of {totalPages}</Text>
          <button onClick={onNext} disabled={page >= totalPages} style={btnStyle(page < totalPages)}>Next →</button>
        </div>
      </div>
    </Card>
  );
}

function btnStyle(active) {
  return {
    padding: '6px 14px',
    border: '1px solid #c9cccf',
    borderRadius: '6px',
    background: active ? '#fff' : '#f6f6f7',
    color: active ? '#202223' : '#8c9196',
    cursor: active ? 'pointer' : 'not-allowed',
    fontSize: '13px',
    fontFamily: 'sans-serif',
  };
}

export default function GroupsPage() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [form, setForm] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [searchValue, setSearchValue] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/attributes/groups');
      setGroups(res.data?.data || []);
    } catch {
      setToast({ content: 'Failed to load groups', error: true });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  // Reset page on filter/limit change
  useEffect(() => { setPage(1); }, [searchValue, limit]);

  const openCreate = () => {
    setForm({ name: '', description: '' });
    setModal('create');
  };

  const openEdit = (group) => {
    setForm({ name: group.name, description: group.description || '' });
    setModal(group);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (modal === 'create') {
        await api.post('/api/attributes/groups', form);
        setToast({ content: 'Group created successfully' });
      } else {
        await api.put(`/api/attributes/groups/${modal.id}`, form);
        setToast({ content: 'Group updated successfully' });
      }
      setModal(null);
      fetchGroups();
    } catch (err) {
      setToast({ content: 'Failed to save group', error: true });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/api/attributes/groups/${deleteModal.id}`);
      setToast({ content: 'Group deleted' });
      setDeleteModal(null);
      fetchGroups();
    } catch {
      setToast({ content: 'Failed to delete group', error: true });
    }
  };

  // Filter
  const filtered = groups.filter(g =>
    !searchValue || g.name.toLowerCase().includes(searchValue.toLowerCase()) ||
    (g.description || '').toLowerCase().includes(searchValue.toLowerCase())
  );

  // Paginate
  const totalPages = Math.max(1, Math.ceil(filtered.length / limit));
  const safePage = Math.min(page, totalPages);
  const start = filtered.length === 0 ? 0 : (safePage - 1) * limit + 1;
  const end = Math.min(safePage * limit, filtered.length);
  const paginated = filtered.slice((safePage - 1) * limit, safePage * limit);

  const rows = paginated.map(g => [
    <Text variant="bodyMd" fontWeight="semibold">{g.name}</Text>,
    <Text variant="bodySm" tone="subdued">{g.description || '—'}</Text>,
    <Badge>{g.attribute_count || 0} attributes</Badge>,
    <InlineStack gap="200">
      <Button size="slim" onClick={() => openEdit(g)}>Edit</Button>
      <Button size="slim" tone="critical" onClick={() => setDeleteModal(g)}>Delete</Button>
    </InlineStack>,
  ]);

  const toastMarkup = toast ? (
    <Toast content={toast.content} error={toast.error} onDismiss={() => setToast(null)} />
  ) : null;

  return (
    <>
      {toastMarkup}
      <Page
        title="Attribute Groups"
        subtitle="Organize attributes into logical groups"
        primaryAction={{ content: 'Create Group', onAction: openCreate }}
      >
        <Layout>
          {/* Search + per-page */}
          <Layout.Section>
            <Card>
              <InlineStack gap="300" wrap blockAlign="center">
                <div style={{ flex: 1, minWidth: 200 }}>
                  <TextField
                    label=""
                    placeholder="Search groups..."
                    value={searchValue}
                    onChange={setSearchValue}
                    clearButton
                    onClearButtonClick={() => setSearchValue('')}
                    autoComplete="off"
                  />
                </div>
                <div style={{ minWidth: 150 }}>
                  <Select
                    label=""
                    options={PER_PAGE_OPTIONS}
                    value={String(limit)}
                    onChange={v => setLimit(Number(v))}
                  />
                </div>
                <Text variant="bodySm" tone="subdued">
                  {filtered.length} group{filtered.length !== 1 ? 's' : ''}
                </Text>
              </InlineStack>
            </Card>
          </Layout.Section>

          {/* Table */}
          <Layout.Section>
            <Card padding="0">
              {loading ? (
                <Box padding="800">
                  <div style={{ display: 'flex', justifyContent: 'center' }}><Spinner size="large" /></div>
                </Box>
              ) : filtered.length === 0 ? (
                <EmptyState
                  heading="No groups yet"
                  action={{ content: 'Create Group', onAction: openCreate }}
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <p>Groups help organize related attributes together on the product page.</p>
                </EmptyState>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text']}
                  headings={['Name', 'Description', 'Attributes', 'Actions']}
                  rows={rows}
                  hoverable
                />
              )}
            </Card>
          </Layout.Section>

          {/* Pagination */}
          {!loading && (
            <Layout.Section>
              <PaginationBar
                page={safePage}
                totalPages={totalPages}
                total={filtered.length}
                start={start}
                end={end}
                onPrev={() => setPage(p => Math.max(1, p - 1))}
                onNext={() => setPage(p => Math.min(totalPages, p + 1))}
              />
            </Layout.Section>
          )}
        </Layout>

        {/* Create/Edit Modal */}
        <Modal
          open={!!modal}
          onClose={() => setModal(null)}
          title={modal === 'create' ? 'Create Group' : 'Edit Group'}
          primaryAction={{ content: saving ? 'Saving...' : 'Save', onAction: handleSave, loading: saving }}
          secondaryActions={[{ content: 'Cancel', onAction: () => setModal(null) }]}
        >
          <Modal.Section>
            <FormLayout>
              <TextField
                label="Group Name *"
                value={form.name}
                onChange={v => setForm(f => ({ ...f, name: v }))}
                placeholder="e.g. Personalization, Size Options"
                autoComplete="off"
              />
              <TextField
                label="Description"
                value={form.description}
                onChange={v => setForm(f => ({ ...f, description: v }))}
                multiline={3}
                placeholder="Optional description"
                autoComplete="off"
              />
            </FormLayout>
          </Modal.Section>
        </Modal>

        {/* Delete Modal */}
        <Modal
          open={!!deleteModal}
          onClose={() => setDeleteModal(null)}
          title="Delete Group"
          primaryAction={{ content: 'Delete', destructive: true, onAction: handleDelete }}
          secondaryActions={[{ content: 'Cancel', onAction: () => setDeleteModal(null) }]}
        >
          <Modal.Section>
            <Text>Delete group <strong>{deleteModal?.name}</strong>? Attributes in this group will be ungrouped.</Text>
          </Modal.Section>
        </Modal>
      </Page>
    </>
  );
}
